// Collecte des cibles d'alignement depuis le DOM, une seule fois au début du
// drag (les box voisines ne bougent pas pendant qu'on en déplace une).
//
// Pourquoi le DOM plutôt que le store : une box en hauteur `auto` (texte,
// titre, image ratio) n'a pas de hauteur exploitable dans le store, et une box
// pivotée a une emprise visuelle différente de son left/top. getBoundingClientRect
// donne l'emprise réellement vue par l'utilisateur — c'est sur elle qu'il aligne.
//
// Conséquence assumée : on raisonne en boîte englobante (AABB). Le delta calculé
// est ensuite appliqué tel quel à box.left/box.top, donc une box pivotée
// s'accroche par son emprise, pas par son origine — ce qui est le comportement
// attendu.

import { SNAP_CONFIG } from './snapConfig';

const BOX_PREFIX = 'box-container-';

// Le repère des box est leur parent DOM direct (div interne de Column) : leurs
// left/top sont relatifs à LUI, pas au wrapper — mesurer le wrapper décalerait
// les guides de l'écart de positionnement d'un masque dans son wrapper.
//
// On remonte depuis l'élément réellement déplacé plutôt que par
// getElementById(columnId) : un même masque peut être rendu plusieurs fois
// simultanément (aperçu, modale de configuration), et l'id ne désignerait alors
// pas forcément la copie éditée.
const resolveElements = ({ draggedEl, draggedId }) => {
  const dragged = draggedEl ?? document.getElementById(`${BOX_PREFIX}${draggedId}`);
  return { dragged, parent: dragged?.parentElement ?? null };
};

// Viewport élargi : une box juste hors écran reste une cible crédible pendant
// qu'on scrolle en fin de drag.
const visibleArea = () => {
  const margin = SNAP_CONFIG.viewportMargin;
  return {
    left: -margin,
    top: -margin,
    right: (window.innerWidth || 0) + margin,
    bottom: (window.innerHeight || 0) + margin,
  };
};

const intersects = (rect, area) =>
  rect.right >= area.left &&
  rect.left <= area.right &&
  rect.bottom >= area.top &&
  rect.top <= area.bottom;

/**
 * @param {HTMLElement} [draggedEl] nœud de la box déplacée (chemin nominal)
 * @param {string} draggedId id de la box déplacée (repli si le nœud n'est pas fourni)
 * @param {string[]} excludeIds ids à ne pas prendre comme cibles (la box elle-même, et les membres d'un groupe)
 * @param {number} zoomFactor échelle CSS appliquée au document
 * @param {{left:number,right:number,top:number,bottom:number}} [margins] marges de page, en px document
 * @returns {{dragged:object, targets:object[]}|null} null si le DOM attendu est absent
 */
export const collectSnapTargets = ({
  draggedEl,
  draggedId,
  excludeIds = [],
  zoomFactor,
  margins,
}) => {
  const { dragged: draggedNode, parent: parentEl } = resolveElements({ draggedEl, draggedId });
  if (!parentEl || !draggedNode || !zoomFactor) return null;

  const parentRect = parentEl.getBoundingClientRect();
  const toLocal = (rect) => ({
    x: (rect.left - parentRect.left) / zoomFactor,
    y: (rect.top - parentRect.top) / zoomFactor,
    width: rect.width / zoomFactor,
    height: rect.height / zoomFactor,
  });

  const dragged = toLocal(draggedNode.getBoundingClientRect());
  const area = visibleArea();
  const excluded = new Set(excludeIds);
  const targets = [];

  parentEl.querySelectorAll(`[id^="${BOX_PREFIX}"]`).forEach((element) => {
    const id = element.id.slice(BOX_PREFIX.length);
    if (excluded.has(id)) return;
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;
    if (SNAP_CONFIG.viewportCulling && !intersects(rect, area)) return;
    targets.push({ id, ...toLocal(rect) });
  });

  // Axes majeurs de la zone de travail. Une bbox suffit à les exprimer : ses
  // bords et son centre sont exactement les alignements attendus (bords de
  // page + centre de page, puis bords de la zone de contenu + son centre).
  const page = {
    id: '__page__',
    canvas: true,
    x: 0,
    y: 0,
    width: parentRect.width / zoomFactor,
    height: parentRect.height / zoomFactor,
  };
  targets.push(page);
  if (margins) {
    const contentWidth = page.width - margins.left - margins.right;
    const contentHeight = page.height - margins.top - margins.bottom;
    // Sur une zone plus courte que ses marges (masque en-tête/pied de page),
    // la zone de contenu n'existe pas : seuls les bords de la zone comptent.
    if (contentWidth > 0 && contentHeight > 0) {
      targets.push({
        id: '__content__',
        canvas: true,
        x: margins.left,
        y: margins.top,
        width: contentWidth,
        height: contentHeight,
      });
    }
  }

  return { dragged, targets };
};
